import { HotelStayCalendarComponent } from '../stay-calendar/stay-calendar.component';
import { WindowRefService } from '../../../../services/window-ref.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import { EventBusService } from "../../../../services/event-bus.service";
import { NgModalComponent } from "../../../ui/modal/modal.component";
import { HotelPriceCalendarComponent } from "../calendar/calendar.component";
import { TouristsComponent } from "../tourists/tourists.component";
import { ShortSearchParamsFacade } from "../short-search-params.facade";

@Component({
  selector: "app-short-search-params-mobile",
  templateUrl: "./short-search-params-mobile.component.html",
  styleUrls: ["./short-search-params-mobile.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgModalComponent, TouristsComponent, HotelPriceCalendarComponent, HotelStayCalendarComponent],
})
export class ShortSearchParamsMobileComponent implements OnInit, OnDestroy {
  @ViewChild(NgModalComponent) ngModal!: NgModalComponent;

  @ViewChild(HotelStayCalendarComponent) stayCalendar: HotelStayCalendarComponent;

  openedModal: string | null = null;
  activeModalConfiguration: string | null = null;
  isFooterVisible = false;
  private footerObserver: IntersectionObserver | null = null;
  private calendarPlace = "short-search-params";
  private subscriptions: Subscription[] = [];

  constructor(
    public facade: ShortSearchParamsFacade,
    private eventBusService: EventBusService,
    private renderer: Renderer2,
    private cdRef: ChangeDetectorRef,
    private windowRef: WindowRefService,
  ) {}

  ngOnInit() {
    const footer = document.querySelector('.mobile-footer');
    if (footer) {
      this.footerObserver = new IntersectionObserver(([entry]) => {
        this.isFooterVisible = entry.isIntersecting;
        this.cdRef.markForCheck();
      });
      this.footerObserver.observe(footer);
    }

    this.subscriptions.push(
      this.eventBusService.isCloseModal$.subscribe(() => {
        this.openedModal = null;
        this.facade.unlockMobileCalendarSelection();
        this.cdRef.detectChanges();
      }),
      this.facade.viewStateChanged$.subscribe(() => {
        this.cdRef.detectChanges();
      }),
      this.eventBusService.openCalendarFromEmptyResult$.subscribe(() => {
        if (this.facade.newSearchParams?.hotelsSearch) {
          this.openModal('stayCalendar');
        } else {
          this.openCalendar('empty-result');
        }
      }),
      this.eventBusService.openPriceCalendarFromLiner$.subscribe(() => {
        this.openCalendar("price-calendar-liner");
      })
    );
  }

  ngOnDestroy() {
    this.footerObserver?.disconnect();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  onDatesButtonClick() {
    if (this.facade.newSearchParams?.hotelsSearch) {
      this.openModal("stayCalendar");
      return;
    }
    this.openCalendar("short-search-params");
  }

  onPriceCalendarButtonClick() {
    this.openCalendar("sticky-button");
  }

  openModal(key: string) {
    if (key === "calendar") {
      this.facade.prepareMobileCalendarOpen(() => this.showModal(key));
      return;
    }

    this.showModal(key);
  }

  private openCalendar(place: string) {
    if (this.facade.newSearchParams?.hotelsSearch) {
      return;
    }
    this.calendarPlace = place;
    if (place !== "price-calendar-liner") {
      this.eventBusService.priceCalendarLinerSelection$.next(null);
      this.eventBusService.resetPriceCalendarLinerSelection$.next();
    }
    this.publishAppliedCalendarSelection();
    this.facade.trackCalendarOpen(place);
    this.openModal("calendar");
  }

  private publishAppliedCalendarSelection(): void {
    if (!this.facade.dateFrom || typeof this.facade.nightsFrom !== "number") {
      this.eventBusService.priceCalendarAppliedSelection$.next(null);
      return;
    }

    this.eventBusService.priceCalendarAppliedSelection$.next({
      date: this.facade.dateFrom,
      nights: this.facade.nightsFrom,
    });
  }

  private showModal(key: string) {
    this.activeModalConfiguration = key;
    this.openedModal = this.activeModalConfiguration;

    this.cdRef.detectChanges();
    this.ngModal.open();
    if (key === 'stayCalendar') {
      this.eventBusService.changeModalTitle$.next(this.windowRef.nativeWindow.app.translations.hotelStayCalendar.title);
    }

    const button = document.querySelector(".modal2 .btn-apply");
    if (button) {
      this.renderer.listen(button, "click", (event: MouseEvent) => this.onApply(event));
    }

    if (key === "tourists") {
      this.facade.trackTouristsOpen();
    }
  }

  modalConfiguration() {
    let htmlDetachSelectorId = null;
    let parentCssClass = null;
    let event = "";
    let buttonLabel = "Применить";

    if (this.activeModalConfiguration === "stayCalendar") {
      htmlDetachSelectorId = "stayCalendarModal";
      parentCssClass = "hotel-stay-modal";
      buttonLabel = this.windowRef.nativeWindow.app.translations.hotelStayCalendar.submit;
    } else if (this.activeModalConfiguration === "calendar") {
      htmlDetachSelectorId = "calendarModal";
      parentCssClass = "calendar-modal";
      event = "calendar_modal_is_open_in_tour_listing";
      buttonLabel = "Показать туры";
    } else if (this.activeModalConfiguration === "tourists") {
      htmlDetachSelectorId = "touristsModal";
      parentCssClass = "tourists-modal";
      event = "tourists_count_modal_open";
    } else {
      this.openedModal = null;
    }

    return {
      header: this.activeModalConfiguration === "stayCalendar"
        ? { title: this.windowRef.nativeWindow.app.translations.hotelStayCalendar.title }
        : {},
      content: {
        htmlDetachSelectorId,
      },
      parentCssClass,
      footer: {
        buttonLabel,
        buttonCssClass: "btn-apply"
      },
      onAfterShow: () => {
        if (this.activeModalConfiguration === 'stayCalendar') {
          this.stayCalendar.scrollToActiveDate();
        }
        const eventPayload = event === "calendar_modal_is_open_in_tour_listing"
          ? { Place: this.calendarPlace }
          : undefined;
        if (event) this.facade.trackModalAfterShow(event, eventPayload);
      },
    };
  }

  onApply(event?: Event) {
    const modalId = this.ngModal?.configuration?.content?.htmlDetachSelectorId;

    if (modalId === "stayCalendarModal") {
      if (!this.stayCalendar.canApply) {
        event?.preventDefault();
        event?.stopPropagation();
        return;
      }
      this.eventBusService.shortSearchParams$.next(this.stayCalendar.selection);
    }

    if (modalId === "calendarModal") {
      this.facade.trackCalendarApply();
      this.eventBusService.applyMobilePriceCalendarSelection$.next();
    }

    if (modalId === "touristsModal") {
      this.facade.trackTouristsApply();
    }

    this.facade.apply(modalId === "calendarModal" ? "calendar" : "form");
  }
}
